import React, { useEffect, useState } from "react";
import { CRYPTO_WALLETS } from "../../../constants/wallets";
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../store';
interface ImportData {
    info: {
        mnemonic?: string;
        privateKey?: string;
        address: string;
    };
    currency: string;
    endpoint: string;
}

interface CreateWalletProps {
    token: string;
    isOpen: boolean;
    setOpenImportWallet: (isOpen: boolean) => void;
    addWallet: () => void;
    setImportData: (data: ImportData) => void;
}

export default function ImportWallet({
    token,
    isOpen,
    setOpenImportWallet,
    addWallet,
    setImportData,
}: CreateWalletProps) {
     const { data: { mainWallets, cryptoWallets, masterCard } } = useSelector((state: RootState) => state.assets);
     // Bitcoin only supports private key import
     const onlyPrivateKey = token === "btc";  
    const [wallet, setWallet] = useState<{
        address: string;
        key: string;
        option: "privateKey" | "mnemonic";
    }>({
        address: "",
        key: "",
        option: "privateKey",
    });

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setWallet((prev) => ({
            ...prev,
            [name]: value,
        }));

    };

    const handleOptionChange = (option: "privateKey" | "mnemonic") => {
        setWallet((prev) => ({ ...prev, option }));
    };

    const handleImportWallet = () => {
        if (!wallet.address && !wallet.key) {
            toast.warning("Please fill in both address and key fields.");
            return;
        }
        if (!wallet.address) {
            toast.warning("Please enter wallet address.");
            return;
        }
        if (!wallet.key) {
            toast.warning(`Please enter your ${wallet.option === "privateKey" ? "private key" : "seed phrase"}.`);
            return;
        }     
        addWallet();
    };
    useEffect(()=>{
        const network = CRYPTO_WALLETS.find((item) => item.token === token);
        if (!network) return;

        // Map tokens to their base blockchain for import
        let blockchainName = "";
        const tokenUpper = token.toUpperCase();
        
        if (tokenUpper === "BTC") {
            blockchainName = "bitcoin";
        } else if (tokenUpper === "ETH" || tokenUpper === "USDT_ERC20" || tokenUpper === "USDC") {
            blockchainName = "ethereum";
        } else if (tokenUpper === "BNB" || tokenUpper === "USDT_BEP20") {
            blockchainName = "bnb";
        } else if (tokenUpper === "TRX" || tokenUpper === "USDT_TRON" || tokenUpper === "USDT_TRC20") {
            blockchainName = "tron";
        } else if (tokenUpper === "SOL" || tokenUpper.includes("SPL")) {
            blockchainName = "solana";
        }

        const isMnemonic = wallet.option === "mnemonic";
        const endpoint = `/crypto-web3/restore/${blockchainName}/restore-${isMnemonic ? "mnemonic" : "private-key"}`;
        
        setImportData({
            info: {
                [isMnemonic ? "mnemonic" : "privateKey"]: wallet.key,
                address: wallet.address,
            },
            currency: network.currency,
            endpoint,
        });
    }, [wallet, token, setImportData])
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4 sm:px-6 md:px-8">
            <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 md:p-8 w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
                        Import {token.toUpperCase()} Wallet
                    </h2>
                    <button
                        onClick={() => setOpenImportWallet(false)}
                        className="text-gray-600 hover:text-gray-800"
                    >
                        ✕
                    </button>
                </div>

                <div className="space-y-3 sm:space-y-4">
                    <div className="space-y-4">
                        <div>
                            <label
                                htmlFor="address"
                                className="block text-sm font-medium text-gray-700"
                            >
                                Address
                            </label>
                            <input
                                id="address"
                                type="text"
                                name="address"
                                value={wallet.address}
                                onChange={handleChange}
                                placeholder="Enter address"
                                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-belobaba-gold mb-2"
                                aria-required="true"
                            />
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <div>
                                <input
                                    type="checkbox"
                                    id="privateKeyOption"
                                    name="option"
                                    checked={wallet.option === "privateKey"}
                                    onChange={() => handleOptionChange("privateKey")}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                />
                                <label
                                    htmlFor="privateKeyOption"
                                    className="text-sm text-gray-700 ml-2"
                                >
                                    Private Key
                                </label>
                            </div>
                            {!onlyPrivateKey && (
                            <div>
                                <input
                                    type="checkbox"
                                    id="mnemonicOption"
                                    name="option"
                                    checked={wallet.option === "mnemonic"}
                                    onChange={() => handleOptionChange("mnemonic")}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"  
                                />
                                <label
                                    htmlFor="mnemonicOption"
                                    className="text-sm text-gray-700 ml-2"
                                >
                                    Seed Phrase
                                </label>
                            </div>
                            )}
                        </div>
                        <div>
                            <label
                                htmlFor="key"
                                className="block text-sm font-medium text-gray-700"
                            >
                                {wallet.option === "privateKey" ? "Private Key" : "Seed Phrase (12 or 24 words)"}
                            </label>
                            <textarea
                                id="key"
                                name="key"
                                value={wallet.key}
                                onChange={handleChange}
                                placeholder={wallet.option === "privateKey" 
                                    ? "Enter your private key" 
                                    : "Enter your 12 or 24 word seed phrase"
                                }
                                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-belobaba-gold"
                                rows={4}
                                aria-required="true"
                            />
                        </div>
                        <div className="flex justify-end space-x-2">
                            <button
                                type="button"
                                onClick={() => setOpenImportWallet(false)}
                                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
                                disabled={cryptoWallets.importStatus==="loading"? true:false} 
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleImportWallet}
                                className="px-4 py-2 text-white bg-belobaba-gold rounded-md hover:bg-[#a39a22]"
                            >
                                {cryptoWallets.importStatus==="loading"? "Importing...": "Import"} 
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}